import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, ChevronDown, Calendar } from "lucide-react";
import { format } from "date-fns";
import Loader from "../../../components/Loader";
import { useJibble, formatDuration, formatTime } from "./useJibble";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { Roles, formatDateToISODateString } from "../../../util/constant";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import { log } from "console";

const Jibble: React.FC = () => {
  const [selectedMember, setSelectedMember] = useState<any>(null);

  const {
    loading,
    timesheetData,
    weekDays,
    weekRange,
    error,
    goToPreviousWeek,
    goToNextWeek,
    goToPreviousMonth,
    goToNextMonth,
    goToCurrentMonth,
    getDailyData,
    calculateWeeklyTotal,
    jibbleMembers,
    userRole,
    userSubRole,
    monthlyTimesheetData,
    currentMonth,
    getDaysInMonth,
    fetchProgress,
    approvedLeaves,
  } = useJibble(selectedMember);

  // Check if user is admin or hr (including subrole)
  const isAdminOrHr =
    userRole === Roles.admin ||
    userRole === Roles.hr ||
    userSubRole === Roles.hr;

  // Set default selected member for admin/hr when members are loaded
  useEffect(() => {
    if (isAdminOrHr && jibbleMembers.length > 0 && !selectedMember) {
      setSelectedMember(jibbleMembers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jibbleMembers, isAdminOrHr]);
  const dayLabels = ["M", "T", "W", "T", "F", "S", "S"];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const isRestDay = (date: Date): boolean => {
    const day = date.getDay();
    return day === 0 || day === 6; // Sunday or Saturday
  };

  
  const isToday = (date: Date): boolean => {
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate.getTime() === today.getTime();
  };

  const isOngoing = (date: Date, dailyData: any): boolean => {
    if (!dailyData) return false;
    return (
      isToday(date) &&
      dailyData.firstIn &&
      !dailyData.lastOut &&
      dailyData.tracked !== "PT0S"
    );
  };

  // Helper function to parse "H:MM" format and convert to total minutes
  const parseTimeToMinutes = (timeStr: string): number => {
    if (!timeStr || timeStr === "-") return 0;
    const parts = timeStr.split(":");
    if (parts.length !== 2) return 0;
    const hours = parseInt(parts[0], 10) || 0;
    const minutes = parseInt(parts[1], 10) || 0;
    return hours * 60 + minutes;
  };

  // Helper function to get color based on hours
  const getHoursColor = (hours: string): string => {
    if (!hours || hours === "-") return "";
    const totalMinutes = parseTimeToMinutes(hours);
    const fourThirtyMinutes = 4 * 60 + 30; // 4:30 = 270 minutes
    const eightThirtyMinutes = 8 * 60 + 30; // 8:30 = 510 minutes

    if (totalMinutes < fourThirtyMinutes) {
      return "bg-red-300";
    } else if (totalMinutes < eightThirtyMinutes) {
      return "bg-yellow-300";
    }
    return "";
  };

  const weeklyTotal = calculateWeeklyTotal();

  const monthDays = currentMonth ? getDaysInMonth(currentMonth) : [];
  const monthName = currentMonth ? format(currentMonth, "MMMM yyyy") : "";
  const isMonthlyDataLoading =
    isAdminOrHr &&
    (loading ||
      (monthlyTimesheetData.length === 0 &&
        !error &&
        (userRole !== "" || userSubRole !== "")));

  const isWeeklyDataLoading =
    !isAdminOrHr && (loading || (!timesheetData && !error));

  if (isWeeklyDataLoading) {
    return (
      <div className="h-[87vh] p-3 rounded-lg bg-white flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-[87vh] p-3 rounded-lg bg-white flex items-center justify-center">
        <div className="text-red-500 text-center">
          <p className="text-lg font-semibold">Error loading timesheet</p>
          <p className="text-sm mt-2">{error}</p>
        </div>
      </div>
    );
  }

  // Convert ISO duration (PT8H30M) → total seconds
const parseISODurationToSeconds = (duration: string): number => {
  if (!duration) return 0;

  const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  if (!match) return 0;

  const hours = parseInt(match[1] || "0", 10);
  const minutes = parseInt(match[2] || "0", 10);
  const seconds = parseInt(match[3] || "0", 10);

  return hours * 3600 + minutes * 60 + seconds;
};

// Sum weekly duration
const getWeeklyTotalDuration = (
  key: "tracked" | "payrollHours"
): string => {
  let totalSeconds = 0;

  weekDays.forEach((day) => {
    if (isRestDay(day)) return;
    const dailyData = getDailyData(day);
    if (dailyData?.[key]) {
      totalSeconds += parseISODurationToSeconds(dailyData[key]);
    }
  });

  return totalSeconds > 0 ? formatDuration(`PT${totalSeconds}S`) : "-";
};

  


  // Render monthly view for admin/hr
  if (isAdminOrHr) {
    return (
      <div className="h-[100vh] p-6 rounded-lg bg-white overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold text-gray-800">
                Monthly Timesheets
              </h1>
              <ChevronDown className="w-5 h-5 text-gray-600 cursor-pointer" />
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={goToPreviousMonth}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Previous month"
                disabled={loading}
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>

              <div className="flex items-center gap-2">
                <span className="text-[11px] font-medium text-gray-700">
                  {monthName}
                </span>
                <Calendar className="w-4 h-4 text-gray-500" />
              </div>
              <button
                onClick={goToNextMonth}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Next month"
                disabled={loading}
              >
                <ChevronRight className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>
        </div>

        {isMonthlyDataLoading ? (
          <div className="flex items-center justify-center h-[60vh]">
            <div className="flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-200 border-t-blue-600"></div>
              {fetchProgress && fetchProgress.total > 0 ? (
                <div className="text-sm text-gray-600">
                  <span>Loading timesheet data...</span>
                </div>
              ) : (
                <div className="text-sm text-gray-600">
                  <span>Loading timesheet data...</span>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Monthly Timesheet Table */
          <div className="overflow-x-auto h-[80vh]">
            <table className="min-w-[50vw] border-collapse">
              <thead>
                <tr>
                  <th className="font-medium text-gray-600 border-b sticky left-0 bg-white z-10 text-[10px] text-center border w-0">
                    First Name
                  </th>
                  {monthDays.map((day, index) => {
                    const isWeekend = day.getDay() === 0 || day.getDay() === 6; // Sunday or Saturday
                    return (
                      <th
                        key={index}
                        className={`text-center font-medium text-gray-600 border-b min-w-[43px] text-[11px] border w-0 ${
                          isWeekend ? "bg-gray-100" : ""
                        }`}
                      >
                        <div className="flex flex-col items-center">
                          <span className="text-xs">{format(day, "d")}</span>
                          <span className={`text-xs text-gray-500 mt-1`}>
                            {format(day, "EEE")}
                          </span>
                        </div>
                      </th>
                    );
                  })}
                  <th className="text-center border font-medium text-gray-600 border-b bg-gray-50 text-[11px]">
                    Total Monthly Hours
                  </th>
                </tr>
              </thead>
              <tbody>
                {monthlyTimesheetData.length === 0 ? (
                  <tr>
                    <td
                      colSpan={monthDays.length + 2}
                      className="p-4 text-center text-gray-500"
                    >
                      No timesheet data available
                    </td>
                  </tr>
                ) : (
                  monthlyTimesheetData.map((user, userIndex) => (
                    <tr key={user.personId} className="hover:bg-gray-50">
                      <td className="font-medium text-gray-700 border-b sticky left-0 bg-white z-10 text-[11px] border text-center">
                        {user.fullName}
                      </td>
                      {monthDays.map((day, dayIndex) => {
                        const dateStr = formatDateToISODateString(day);
                        const hours = user.dailyHours[dateStr] || "-";
                        const hoursColor = getHoursColor(hours);
                        const leaveInfo =
                          approvedLeaves[user.personId]?.[dateStr];

                        const hasHours =
                          hours && hours !== "-" && hours !== "0:00";

                        const isOnLeave = !!leaveInfo && !hasHours;
                        const leaveDescription = leaveInfo?.description || "";
                        const isWeekend = day.getDay() === 0 || day.getDay() === 6; // Sunday or Saturday
                        
                        // Determine cell color: leave takes priority, then hours color, then weekend gray
                        let cellColorClass = "";
                        if (isOnLeave) {
                          cellColorClass = "bg-red-300";
                        } else if (hoursColor) {
                          cellColorClass = hoursColor;
                        } else if (isWeekend) {
                          cellColorClass = "bg-gray-100";
                        }
                        
                        const cellContent = isOnLeave ? "Leave" : hours;
                        return (
                          <td
                            key={dayIndex}
                            className={`text-center text-[10px] border ${cellColorClass} ${
                              isOnLeave ? "cursor-help relative group" : ""
                            }`}
                            title={
                              isOnLeave && leaveDescription
                                ? leaveDescription
                                : undefined
                            }
                          >
                            {cellContent}
                          </td>
                        );
                      })}
                      <td className="p-3 text-center font-semibold text-gray-800 border-b bg-gray-50 text-[11px] border">
                        {user.totalMonthlyHours}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // Regular weekly view for non-admin/hr users
  return (
    <div className="h-[87vh] p-6 rounded-lg bg-white overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-gray-800">
              Weekly Timesheets
            </h1>
            <ChevronDown className="w-5 h-5 text-gray-600 cursor-pointer" />
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={goToPreviousWeek}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Previous week"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>

            <div className="flex items-center gap-2 ml-2">
              <span className="text-sm font-medium text-gray-700">
                {weekRange}
              </span>
              <Calendar className="w-4 h-4 text-gray-500" />
            </div>
            <button
              onClick={goToNextWeek}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Next week"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      {/* Timesheet Table */}
      <div
        className="overflow-x-auto"
        key={
          selectedMember?.personId ||
          selectedMember?.id ||
          selectedMember?.code ||
          "default"
        }
      >
        <table className="w-full border-collapse">
          {/* Header Row - Days */}
          <thead>
            <tr>
              <th className="text-left p-3 font-medium text-gray-600 border-b"></th>
              {weekDays.map((day, index) => {
                return (
                  <th
                    key={index}
                    className="text-center p-3 font-medium border-b text-gray-600"
                  >
                    <div className="flex flex-col items-center">
                      <span className="text-sm">{dayLabels[index]}</span>
                      <span className="text-xs mt-1">{format(day, "d")}</span>
                    </div>
                  </th>
                );
              })}

              <th className="text-center p-3 font-medium text-gray-600 border-b">
                Total
              </th>
            </tr>
          </thead>

          <tbody>
            {/* Rest Days Row */}
            <tr>
              <td className="p-3"></td>
              {weekDays.map((day, index) => {
                const isSat = day.getDay() === 6;
                const isSun = day.getDay() === 0;

                if (isSat) {
                  return (
                    <td
                      key={index}
                      colSpan={2}
                      className="p-2 text-center text-sm text-gray-600 border-b"
                    >
                      Rest day
                    </td>
                  );
                }
                if (isSun) {
                  return null; // Already covered by Saturday's colspan
                }
                return <td key={index} className="p-3 border-b"></td>;
              })}
              <td className="p-3 border-b"></td>
            </tr>

            {/* First In Row */}
            <tr>
              <td className="p-3 font-medium text-gray-700 border-b">
                First in
              </td>
              {weekDays.map((day, index) => {
                if (isRestDay(day)) {
                  return (
                    <td key={index} className="p-3 text-center border-b">
                      -
                    </td>
                  );
                }
                const dailyData = getDailyData(day);
                const firstIn = dailyData?.firstIn
                  ? formatTime(dailyData.firstIn)
                  : "-";
                return (
                  <td
                    key={index}
                    className="p-3 text-center text-sm text-gray-700 border-b"
                  >
                    {firstIn}
                  </td>
                );
              })}
              <td className="p-3 text-center border-b">-</td>
            </tr>

            {/* Last Out Row */}
            <tr>
              <td className="p-3 font-medium text-gray-700 border-b">
                Last out
              </td>
              {weekDays.map((day, index) => {
                if (isRestDay(day)) {
                  return (
                    <td key={index} className="p-3 text-center border-b">
                      -
                    </td>
                  );
                }
                const dailyData = getDailyData(day);
                const isOngoingDay = isOngoing(day, dailyData);
                const lastOut = isOngoingDay
                  ? "ongoing"
                  : dailyData?.lastOut
                  ? formatTime(dailyData.lastOut)
                  : "-";
                return (
                  <td
                    key={index}
                    className="p-3 text-center text-sm text-gray-700 border-b"
                  >
                    {lastOut}
                  </td>
                );
              })}
              <td className="p-3 text-center border-b">-</td>
            </tr>

            {/* Tracked Hours Row */}
            <tr>
              <td className="p-3 font-medium text-gray-700 border-b">
                Tracked hours
              </td>
              {weekDays.map((day, index) => {
                if (isRestDay(day)) {
                  return (
                    <td key={index} className="p-3 text-center border-b">
                      -
                    </td>
                  );
                }
                const dailyData = getDailyData(day);
                const tracked = dailyData?.tracked
                  ? formatDuration(dailyData.tracked)
                  : "-";
                return (
                  <td
                    key={index}
                    className="p-3 text-center text-sm text-gray-700 border-b"
                  >
                    {tracked}
                  </td>
                );
              })}
              <td className="p-3 text-center border-b">
                <div className="flex items-center justify-center">
                {getWeeklyTotalDuration("tracked")}
                </div>
              </td>
            </tr>

            {/* Payroll Hours Row */}
            <tr>
              <td className="p-3 font-medium text-gray-700 border-b">
                Payroll hours
              </td>
              {weekDays.map((day, index) => {
                if (isRestDay(day)) {
                  return (
                    <td key={index} className="p-3 text-center border-b">
                      -
                    </td>
                  );
                }
                const dailyData = getDailyData(day);
                const payroll = dailyData?.payrollHours
                  ? formatDuration(dailyData.payrollHours)
                  : "-";
                return (
                  <td
                    key={index}
                    className="p-3 text-center text-sm text-gray-700 border-b"
                  >
                    {payroll}
                  </td>
                );
              })}
              <td className="p-3 text-center border-b">
                <div className="flex items-center justify-center">
                {getWeeklyTotalDuration("payrollHours")}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Weekly Total */}
      <div className="mt-6 flex justify-end">
        <div className="text-right">
          <span className="text-sm font-medium text-gray-600">
            Weekly total
          </span>
          <span className="ml-3 text-sm font-semibold text-gray-800">
            {weeklyTotal.tracked}
          </span>
        </div>
      </div>
    </div>
  );
};

export default Jibble;